/**
 * A spreadsheet brought to Buddy (ai-plan-builder R26 to R31; boards T, T2, T3): the drop zone in
 * its three states, a refused file with how to fix it, and the sheet picker.
 *
 * The file is never parsed here (R31 is the server's): it goes to the listing route, which calls no
 * model and spends nothing (R27), and the chosen sheets go with the same file to the import route,
 * which queues the turn. From there the thread draws the import like any build: its log, the values
 * the plan cannot hold, the unresolved names and the draft card.
 *
 * The zone follows `features/create-exercise`'s `DropZone`: one button that opens the chooser and
 * takes a dropped file, with a hidden file input behind it.
 */

import clsx from 'clsx';
import { useEffect, useId, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { AiImportRefusalReason, AiImportSchedulingMode, AiRefusal, AiSheetListing, AiValueToDecide } from '~/entities/ai-conversation';
import { CheckGlyph } from '~/shared/ui/FormGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { SHEET_ACCEPT } from '../config/importLimits';
import { acceptedFileWords, choiceRefusalWords, isChoiceRefusal, refusalWords } from '../lib/importWording';
import { pickerState, type PickerSheet } from '../model/sheetPicker';

import { ErrorGlyph, SheetGlyph } from './ChatGlyphs';
import { ChoiceQuestion } from './QuestionsCard';
import surfaces from './surfaces.module.css';
import messages from './Messages.module.css';
import styles from './SheetImport.module.css';

type Step =
  | { readonly kind: 'zone' }
  | { readonly kind: 'reading'; readonly fileName: string }
  | { readonly kind: 'refused'; readonly reason: AiImportRefusalReason; readonly limit: number | null }
  | {
      readonly kind: 'choosing';
      readonly file: File;
      readonly fileName: string;
      readonly sheets: readonly PickerSheet[];
      readonly chosen: ReadonlySet<string>;
      /** How the plan runs, where no chosen sheet names weekdays (R54); `null` until picked. */
      readonly mode: AiImportSchedulingMode | null;
      /** The server refused the choice for want of a mode (`ai_import_mode_required`). */
      readonly modeRequired: boolean;
      readonly sending: boolean;
      /** Why the server refused the choice last sent, worded; the picker stays for another. */
      readonly refusal: string | null;
    };

/** What became of the chosen sheets: queued, or refused (the refusal the picker words, if it is its own). */
export type SheetImportOutcome =
  | { readonly outcome: 'sent' }
  | { readonly outcome: 'refused'; readonly refusal: AiRefusal };

export interface SheetImportProps {
  /**
   * The sheets of a file, or why it is not read. A refusal that is not the file's (Buddy switched
   * off, no connection) is the conversation's to say; the zone simply stands again.
   */
  readonly onList: (file: File) => Promise<AiSheetListing>;
  /** The chosen sheets imported, with how the plan runs where the picker asked it. */
  readonly onImport: (file: File, sheets: readonly string[], schedulingMode: AiImportSchedulingMode | null) => Promise<SheetImportOutcome>;
  /** Nothing is running in the conversation: an import may start. */
  readonly ready: boolean;
  /** A file is being dragged over the conversation: the zone draws its hover. */
  readonly dragging: boolean;
  /** The import is put away: „Anuluj", Escape, or a choice sent. */
  readonly onClose: () => void;
  /** A file dropped onto the conversation outside the zone, taken as if it was dropped on it. */
  readonly dropped: File | null;
}

export function SheetImport({ onList, onImport, ready, dragging, onClose, dropped }: SheetImportProps) {
  const { t, i18n } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>({ kind: 'zone' });
  const [over, setOver] = useState(false);
  const titleId = useId();
  const hintId = useId();
  const tooLargeId = useId();
  const modeId = useId();
  const zone = useRef<HTMLButtonElement>(null);
  // The zone takes focus when it opens, so the keyboard is where the import is.
  useEffect(() => {
    zone.current?.focus();
  }, []);
  // Escape anywhere in the import puts it away, the zone and the picker alike.
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    onClose();
  };

  const take = (file: File | undefined) => {
    if (file === undefined) return;
    setStep({ kind: 'reading', fileName: file.name });
    void onList(file).then((listing) => {
      if (listing.outcome === 'refused') {
        const { refusal } = listing;
        setStep(refusal.kind === 'import' ? { kind: 'refused', reason: refusal.reason, limit: refusal.limit } : { kind: 'zone' });
        return;
      }
      const sheets = listing.spreadsheet.sheets.map(
        (sheet): PickerSheet => ({ name: sheet.name, rows: sheet.rows, importable: sheet.importable, namesWeekdays: sheet.namesWeekdays ?? null }),
      );
      setStep({
        kind: 'choosing',
        file,
        fileName: listing.spreadsheet.fileName,
        sheets,
        // Every sheet that can be read is chosen to begin with; the trainer leaves out logs and calculators.
        chosen: new Set(sheets.filter((sheet) => sheet.importable).map((sheet) => sheet.name)),
        mode: null,
        modeRequired: false,
        sending: false,
        refusal: null,
      });
    });
  };

  // A file dropped anywhere on the conversation is this zone's, once.
  const [taken, setTaken] = useState<File | null>(null);
  if (dropped !== null && dropped !== taken) {
    setTaken(dropped);
    take(dropped);
  }

  if (step.kind === 'choosing') {
    const count = step.chosen.size;
    const picker = pickerState(step.sheets, step.chosen, step.mode, step.modeRequired);
    // The wave-1 scheduling question, word for word (R54).
    const modeQuestion = {
      id: 'schedulingMode',
      text: t('web:aiPlan.import.picker.mode.question'),
      options: [
        { id: 'DateBased', label: t('web:aiPlan.import.picker.mode.dateBased') },
        { id: 'SelfPaced', label: t('web:aiPlan.import.picker.mode.selfPaced') },
      ],
    };
    return (
      <div className={messages.buddy} onKeyDown={onKeyDown}>
        {/* Buddy's mark stands only beside work under way; its place is kept. */}
        <span className={messages.mark} aria-hidden="true" />
        <div className={messages.body}>
          <p className={messages.text}>
            <Trans
              i18nKey="web:aiPlan.import.picker.intro"
              values={{ name: step.fileName, count: step.sheets.length }}
              count={step.sheets.length}
              components={{ b: <b /> }}
            />
          </p>
          <fieldset className={clsx(surfaces.card, styles.picker)} aria-labelledby={titleId}>
            <legend id={titleId} className={hidden.srOnly}>
              {t('web:aiPlan.import.picker.label')}
            </legend>
            {step.sheets.map((sheet) => {
              const on = step.chosen.has(sheet.name);
              return (
                <label key={sheet.name} className={clsx(styles.sheet, !sheet.importable && styles.sheetOff)}>
                  <input
                    type="checkbox"
                    className={styles.check}
                    checked={on}
                    // Refusing, and still focusable: the reason is read with it.
                    aria-disabled={!sheet.importable || step.sending ? 'true' : undefined}
                    aria-describedby={sheet.importable ? undefined : tooLargeId}
                    onChange={() => {
                      if (!sheet.importable || step.sending) return;
                      const chosen = new Set(step.chosen);
                      if (on) chosen.delete(sheet.name);
                      else chosen.add(sheet.name);
                      setStep({ ...step, chosen });
                    }}
                  />
                  <span className={clsx(styles.box, on && styles.boxOn)} aria-hidden="true">
                    {on && <CheckGlyph className={styles.boxGlyph} />}
                  </span>
                  <span className={styles.sheetName}>{sheet.name}</span>
                  <span className={styles.sheetMeta} id={sheet.importable ? undefined : tooLargeId}>
                    {sheet.importable ? t('web:aiPlan.import.picker.rows', { count: sheet.rows }) : t('web:aiPlan.import.picker.tooLarge')}
                  </span>
                </label>
              );
            })}
            {picker.asksMode && (
              <div className={styles.mode}>
                <ChoiceQuestion
                  question={modeQuestion}
                  name={modeId}
                  legendId={`${modeId}-legend`}
                  picked={step.mode}
                  onPick={(optionId) => {
                    if (step.sending) return;
                    setStep({ ...step, mode: optionId === 'SelfPaced' ? 'SelfPaced' : 'DateBased' });
                  }}
                />
              </div>
            )}
            {step.refusal !== null && (
              <p className={clsx(surfaces.notice, styles.pickerRefusal)} role="alert">
                <ErrorGlyph className={surfaces.noticeGlyph} />
                <span>{step.refusal}</span>
              </p>
            )}
            <div className={styles.foot}>
              <button
                type="button"
                className={clsx(surfaces.button, surfaces.ghost)}
                onClick={() => {
                  onClose();
                }}
              >
                {t('web:aiPlan.import.picker.cancel')}
              </button>
              <button
                type="button"
                className={clsx(surfaces.button, surfaces.primary)}
                aria-disabled={picker.blocker === null && ready && !step.sending ? undefined : 'true'}
                aria-describedby={
                  picker.blocker === 'chooseSheets' ? hintId : picker.blocker === 'chooseMode' ? `${modeId}-legend` : undefined
                }
                onClick={() => {
                  if (picker.blocker !== null || !ready || step.sending) return;
                  setStep({ ...step, sending: true, refusal: null });
                  void onImport(step.file, [...step.chosen], picker.sends).then((outcome) => {
                    if (outcome.outcome === 'sent') {
                      onClose();
                      return;
                    }
                    const { refusal } = outcome;
                    // A refusal of this choice is said here, with what to change; anything else is the
                    // conversation's notice, and the choice stays as it was.
                    // A missing mode is answered by asking it here.
                    const modeRequired = refusal.kind === 'import' && refusal.reason === 'modeRequired';
                    setStep({
                      ...step,
                      sending: false,
                      modeRequired: step.modeRequired || modeRequired,
                      refusal:
                        refusal.kind === 'import' && isChoiceRefusal(refusal.reason) && !modeRequired
                          ? choiceRefusalWords(t, refusal.reason, refusal.sheet)
                          : null,
                    });
                  });
                }}
              >
                {t('web:aiPlan.import.picker.build', { count })}
              </button>
              {count === 0 && (
                <span id={hintId} className={hidden.srOnly}>
                  {t('web:aiPlan.import.picker.chooseFirst')}
                </span>
              )}
            </div>
          </fieldset>
        </div>
      </div>
    );
  }

  const reading = step.kind === 'reading';
  const hovering = over || dragging;
  return (
    <div className={styles.import} onKeyDown={onKeyDown}>
      {step.kind === 'refused' && <ImportRefusal reason={step.reason} limit={step.limit} locale={i18n.language} />}
      <button
        ref={zone}
        type="button"
        className={clsx(styles.drop, hovering && !reading && styles.dropOver)}
        aria-busy={reading ? 'true' : undefined}
        onClick={() => {
          if (!reading) input.current?.click();
        }}
        onDragOver={(event: DragEvent) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => {
          setOver(false);
        }}
        onDrop={(event: DragEvent) => {
          event.preventDefault();
          event.stopPropagation();
          setOver(false);
          if (!reading) take(event.dataTransfer.files[0]);
        }}
      >
        {reading ? <span className={surfaces.spinner} aria-hidden="true" /> : <SheetGlyph className={styles.dropGlyph} />}
        <b className={styles.dropTitle}>
          {reading
            ? t('web:aiPlan.import.drop.reading', { name: step.fileName })
            : hovering
              ? t('web:aiPlan.import.drop.over')
              : t('web:aiPlan.import.drop.rest')}
        </b>
        <span className={styles.dropHint}>
          {reading
            ? t('web:aiPlan.import.drop.readingHint')
            : hovering
              ? t('web:aiPlan.import.drop.overHint')
              : acceptedFileWords(t, i18n.language)}
        </span>
      </button>
      <button type="button" className={styles.zoneCancel} onClick={onClose}>
        {t('web:aiPlan.import.picker.cancel')}
      </button>
      <input
        ref={input}
        type="file"
        accept={SHEET_ACCEPT}
        hidden
        onChange={(event) => {
          take(event.target.files?.[0]);
          // Cleared, so the same file can be chosen again after a refusal.
          event.target.value = '';
        }}
      />
    </div>
  );
}

/** A file not read, with how to fix it (board T2). */
function ImportRefusal({ reason, limit, locale }: { readonly reason: AiImportRefusalReason; readonly limit: number | null; readonly locale: string }) {
  const { t } = useTranslation();
  const words = refusalWords(t, locale, reason, limit);
  return (
    <p className={surfaces.notice} role="alert">
      <ErrorGlyph className={surfaces.noticeGlyph} />
      <span>
        <b>{words.title}</b> {words.fix}
      </span>
    </p>
  );
}

/** The values of an imported sheet the plan cannot hold, for the trainer to decide (R28; board T). */
export function ValuesToDecide({ values }: { readonly values: readonly AiValueToDecide[] }) {
  const { t } = useTranslation();
  return (
    <>
      <p className={messages.text}>{t('web:aiPlan.import.values.intro')}</p>
      <ul className={clsx(surfaces.card, styles.values)} aria-label={t('web:aiPlan.import.values.label')}>
        {values.map((value) => (
          <li key={`${value.sheet}!${value.cell}`} className={styles.value}>
            <span className={styles.valueWhere}>{t('web:aiPlan.import.values.where', { sheet: value.sheet, cell: value.cell })}</span>
            <span className={styles.valueText}>{value.value}</span>
            <span className={styles.valueNote}>{value.note}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
